import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMyAvatarSync } from "../state/useMyAvatarSync.js";
import { isTypingTarget } from "../world/camera/input.js";
import { useWorldT } from "../world/i18n.js";
import { WorldView } from "../world/index.js";
import { effectiveLayout, layoutStore, useLayout } from "../world/layout.js";
import { NavDrawer } from "../world/NavDrawer.js";
import { playUi } from "../world/sfx.js";
import { WorldDock } from "../world/WorldDock.js";
import { WorldPanel } from "../world/WorldPanel.js";
import "../world/world-game.css";

/** Keys of the game layout: Tab (details panel), F (fullscreen), Esc (close drawers). */
function useGameKeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      const inDrawer =
        e.target instanceof Element &&
        e.target.closest(".world-drawer, .world-navdrawer, .world-dock") !== null;
      if (e.code === "Escape") {
        const s = layoutStore.getState();
        if (s.panelOpen || s.navOpen) {
          layoutStore.dispatch({ type: "closePanel" });
          layoutStore.dispatch({ type: "closeNav" });
          playUi("down");
        }
        return;
      }
      if (e.code === "Tab" && !e.shiftKey && !inDrawer) {
        e.preventDefault();
        const before = layoutStore.getState().panelOpen;
        layoutStore.dispatch({ type: "togglePanel" });
        playUi(before ? "down" : "up");
        return;
      }
      if (e.code === "KeyF" && !e.repeat && !inDrawer) {
        void layoutStore.toggleFullscreen();
        playUi("tick");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}

/**
 * The Dünya page, built like a game: the 3D view fills the page, the details panel and the app
 * menu are drawers (Tab, the menu button), and the whole page goes fullscreen with F. The classic
 * docked layout (sidebar + side panel) stays available in the settings pill.
 */
export function WorldPage() {
  const { t } = useTranslation("web");
  const tw = useWorldT();
  useMyAvatarSync();
  const ls = useLayout();
  const root = useRef<HTMLElement>(null);
  const immersive = effectiveLayout(ls) === "immersive";
  useGameKeys();
  useEffect(() => {
    layoutStore.setRoot(root.current);
    const onChange = () => layoutStore.onFullscreenChange();
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      layoutStore.setRoot(null);
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      // Leaving the page leaves fullscreen and the drawers behind.
      layoutStore.dispatch({ type: "closePanel" });
      layoutStore.dispatch({ type: "closeNav" });
      void layoutStore.exitFullscreen();
    };
  }, []);
  const drawer = immersive && (ls.panelOpen || ls.navOpen);
  return (
    <section
      ref={root}
      aria-labelledby="world-title"
      className={`world-page${immersive ? " is-immersive" : ""}${ls.fs === "pseudo" ? " is-pseudo-fs" : ""}`}
      data-testid="world-page"
      data-layout={immersive ? "immersive" : "docked"}
      data-fs={ls.fs}
    >
      <h1 id="world-title" className="sr-only">
        {t("world.title")}
      </h1>
      <WorldView />
      <WorldDock />
      {drawer ? (
        <div
          className="world-scrim"
          data-testid="world-scrim"
          onPointerDown={() => {
            layoutStore.dispatch({ type: "closePanel" });
            layoutStore.dispatch({ type: "closeNav" });
          }}
        />
      ) : null}
      {immersive && ls.panelOpen ? (
        <div className="world-drawer" data-testid="world-drawer">
          <button
            type="button"
            className="world-glass-btn world-drawer-close"
            aria-label={tw("game.closePanel")}
            onClick={() => layoutStore.dispatch({ type: "closePanel" })}
          >
            ×
          </button>
          <WorldPanel />
        </div>
      ) : null}
      {immersive && ls.navOpen ? <NavDrawer /> : null}
    </section>
  );
}
